import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth";
import type { Role } from "@/types/api";

/**
 * Route guard.
 *
 * 1. Redirects unauthenticated users to /login with a `from` query param
 *    so they can be sent back after login.
 * 2. If `roles` is supplied, also gates by membership role — users whose
 *    current-tenant role isn't in the list get bounced to /dashboard
 *    (their default authed landing). The backend already gates the
 *    relevant API endpoints, so this is UX polish — but it stops the
 *    confusing "page renders, every request 403s" experience for users
 *    who type or bookmark a privileged URL.
 *
 * Until the auth store has hydrated from localStorage on app boot, render
 * nothing — otherwise we'd flash a redirect for users who actually have
 * a valid token.
 */
export function ProtectedRoute({
  children,
  roles,
}: {
  children: ReactNode;
  roles?: Role[];
}) {
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const location = useLocation();

  if (!isHydrated) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (roles && roles.length > 0 && (!role || !roles.includes(role))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
